import type { AudienceMember, BlastPreview, Message } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatPhone, formatQty, formatStamp } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const fill = (s: string, v: Record<string, string | number>) => Object.entries(v).reduce((out, [k, x]) => out.replaceAll(`{${k}}`, String(x)), s);

/** Texts to the business number: reply, make a draft order from one, or mark it dealt with (owner/office/sales). */
export function TextsPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const status = useQuery({ queryKey: qk.messagingStatus, queryFn: ({ signal }) => api.messagingStatus(signal) });
  const inbox = useQuery({ queryKey: qk.messageInbox, queryFn: ({ signal }) => api.messageInbox(signal) });
  const [replying, setReplying] = useState<string | null>(null);
  const [text, setText] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: qk.dashboard });
    void qc.invalidateQueries({ queryKey: qk.signals });
  };
  const handled = useMutation({ mutationFn: (id: string) => api.messageHandled(id), onSuccess: refresh });
  const draft = useMutation({
    mutationFn: (id: string) => api.messageToDraft(id),
    onSuccess: (o) => {
      refresh();
      void qc.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${o.id}`);
    },
  });
  const reply = useMutation({
    mutationFn: (m: Message) => api.sendMessage({ contactId: m.contactId!, body: text.trim(), inReplyTo: m.id }),
    onSuccess: () => {
      setReplying(null);
      setText('');
      refresh();
    },
  });

  return (
    <div className="stack">
      <PageHead title={t('textsTitle')} back="/" />
      <p className="muted">{t('textsHint')}</p>
      {status.data && !status.data.configured ? <p className="note">{t('textsOff')}</p> : null}
      <Link to="/texts/blast" className="btn btn-secondary btn-lg btn-block">
        {t('blastTitle')}
      </Link>
      <section className="card stack-sm">
        <h2>{t('textsInbox')}</h2>
        {draft.error ? <ErrorBox error={draft.error} /> : null}
        {handled.error ? <ErrorBox error={handled.error} /> : null}
        <QueryView query={inbox}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty>{t('textsEmpty')}</Empty>
            ) : (
              <ul className="list" data-testid="inbox">
                {rows.map((m) => (
                  <li key={m.id} className="count-row stack-sm">
                    <div className="row count-head">
                      <span className="row-main">
                        <span className="row-title">
                          {m.customerId ? <Link to={`/customers/${m.customerId}`}>{m.customerName}</Link> : <span className="badge badge-warn">{t('unknownNumber')}</span>}{' '}
                          <span className="label">{m.contactName ?? formatPhone(m.phone)}</span>
                        </span>
                        <span className="row-sub">{formatStamp(m.createdAt, intlLocale, 'day')}</span>
                      </span>
                    </div>
                    <p className="pre">{m.body}</p>
                    <div className="quick-grid">
                      {m.customerId && status.data?.reader ? (
                        <button type="button" className="btn btn-primary" onClick={() => draft.mutate(m.id)} disabled={draft.isPending}>
                          {t('makeDraft')}
                        </button>
                      ) : null}
                      {m.contactId ? (
                        <button type="button" className="btn btn-secondary" onClick={() => setReplying(replying === m.id ? null : m.id)}>
                          {t('reply')}
                        </button>
                      ) : null}
                      <button type="button" className="btn btn-ghost" onClick={() => handled.mutate(m.id)} disabled={handled.isPending}>
                        {t('markHandled')}
                      </button>
                    </div>
                    {replying === m.id ? (
                      <form
                        className="stack-sm"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (text.trim()) reply.mutate(m);
                        }}
                      >
                        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('reply')} />
                        {reply.error ? <ErrorBox error={reply.error} /> : null}
                        <button type="submit" className="btn btn-primary" disabled={reply.isPending || !text.trim()}>
                          {reply.isPending ? t('sending') : t('sendText')}
                        </button>
                      </form>
                    ) : null}
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      </section>
    </div>
  );
}

/** One text per person, in their language, with their own price: pick the audience, write both texts, preview, send. */
export function BlastPage() {
  const { t, intlLocale } = useLocale();
  const items = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal) });
  const [itemId, setItemId] = useState('');
  const [days, setDays] = useState(60);
  const audience = useQuery({ queryKey: qk.audience(itemId, days), queryFn: ({ signal }) => api.audience({ ...(itemId ? { item: itemId } : {}), days }, signal) });
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [bodyEn, setBodyEn] = useState('Hi {name}, {item} this week at {price}. Reply with your order.');
  const [bodyEs, setBodyEs] = useState('Hola {name}, {item} esta semana a {price}. Responde con tu pedido.');
  const [preview, setPreview] = useState<BlastPreview | null>(null);
  const chosen = useMemo(() => (audience.data ?? []).filter((a) => !excluded.has(a.contactId)), [audience.data, excluded]);
  const body = () => ({ contactIds: chosen.map((a) => a.contactId), ...(itemId ? { itemId } : {}), bodyEn: bodyEn.trim(), bodyEs: bodyEs.trim() });
  const previewM = useMutation({ mutationFn: () => api.blastPreview(body()), onSuccess: setPreview });
  const sendM = useMutation({ mutationFn: () => api.blast(body()) });
  const toggle = (a: AudienceMember) =>
    setExcluded((cur) => {
      const next = new Set(cur);
      if (next.has(a.contactId)) next.delete(a.contactId);
      else next.add(a.contactId);
      return next;
    });

  return (
    <div className="stack">
      <PageHead title={t('blastTitle')} back="/texts" />
      <p className="muted">{t('blastHint')}</p>
      <div className="picker-head">
        <label className="field" style={{ flex: 1 }}>
          <span>{t('blastItem')}</span>
          <select value={itemId} aria-label={t('blastItem')} onChange={(e) => { setItemId(e.target.value); setPreview(null); }}>
            <option value="">{t('blastEveryone')}</option>
            {(items.data ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </label>
        {itemId ? (
          <label className="field">
            <span>{t('blastDays')}</span>
            <select value={days} aria-label={t('blastDays')} onChange={(e) => setDays(Number(e.target.value))}>
              {[14, 30, 60, 90, 180].map((d) => (
                <option key={d} value={d}>
                  {d} {t('days')}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <section className="card stack-sm">
        <h2>
          {t('blastAudience')} · {chosen.length}
        </h2>
        <QueryView query={audience}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list" data-testid="audience">
                {rows.map((a) => (
                  <li key={a.contactId} className="row">
                    <label className="check" style={{ flex: 1 }}>
                      <input type="checkbox" checked={!excluded.has(a.contactId)} onChange={() => toggle(a)} />
                      <span className="row-main">
                        <span className="row-title">{a.customerName}</span>
                        <span className="row-sub">
                          {a.contactName ?? formatPhone(a.phone)} · {a.language.toUpperCase()}
                          {a.price !== null ? ` · ${formatMoney(a.price, intlLocale)}` : ''}
                          {a.lastQty !== null && a.lastDate ? ` · ${formatQty(a.lastQty)} ${t('portalLastTime')} ${formatDate(a.lastDate, intlLocale, 'date')}` : ''}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      </section>
      <label className="field">
        <span>{t('blastEn')}</span>
        <textarea rows={3} value={bodyEn} onChange={(e) => { setBodyEn(e.target.value); setPreview(null); }} />
      </label>
      <label className="field">
        <span>{t('blastEs')}</span>
        <textarea rows={3} value={bodyEs} onChange={(e) => { setBodyEs(e.target.value); setPreview(null); }} />
      </label>
      {previewM.error ? <ErrorBox error={previewM.error} /> : null}
      {sendM.error ? <ErrorBox error={sendM.error} /> : null}
      {sendM.data ? (
        <p className="note" role="status">
          {fill(t('blastSent'), { n: sendM.data.sent, k: sendM.data.skipped.length })}
        </p>
      ) : preview ? (
        <section className="card stack-sm">
          <h2>{t('blastPreview')}</h2>
          <ul className="list" data-testid="preview">
            {preview.map((p) => (
              <li key={p.contactId} className="row">
                <span className="row-main">
                  <span className="row-title">
                    {p.customerName} · {p.contactName ?? ''}
                  </span>
                  <span className="row-sub pre">{p.text}</span>
                </span>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => sendM.mutate()} disabled={sendM.isPending || preview.length === 0}>
            {sendM.isPending ? t('sending') : fill(t('blastSend'), { n: preview.length })}
          </button>
        </section>
      ) : (
        <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => previewM.mutate()} disabled={previewM.isPending || chosen.length === 0 || !bodyEn.trim() || !bodyEs.trim()}>
          {t('blastPreview')}
        </button>
      )}
    </div>
  );
}

/** The thread with one customer and a compose box, on the customer screen. */
export function CustomerTexts({ customerId, contacts }: { customerId: string; contacts: { id: string; name: string | null; smsConsent: boolean; phone: string | null }[] }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: qk.messagingStatus, queryFn: ({ signal }) => api.messagingStatus(signal) });
  const thread = useQuery({ queryKey: qk.messageThread(customerId), queryFn: ({ signal }) => api.messageThread(customerId, signal) });
  const textable = contacts.filter((c) => c.smsConsent && c.phone);
  const [contactId, setContactId] = useState('');
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: () => api.sendMessage({ contactId: contactId || textable[0]!.id, body: text.trim() }),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: qk.messageThread(customerId) });
    },
  });
  if (!status.data?.configured) return null;
  return (
    <section className="card stack-sm">
      <h2>{t('textsThread')}</h2>
      {textable.length ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) send.mutate();
          }}
        >
          {textable.length > 1 ? (
            <select value={contactId || textable[0]!.id} onChange={(e) => setContactId(e.target.value)} aria-label={t('contacts')}>
              {textable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? c.phone}
                </option>
              ))}
            </select>
          ) : null}
          <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} aria-label={t('sendText')} />
          {send.error ? <ErrorBox error={send.error} /> : null}
          <button type="submit" className="btn btn-primary" disabled={send.isPending || !text.trim()}>
            {send.isPending ? t('sending') : t('sendText')}
          </button>
        </form>
      ) : null}
      <QueryView query={thread}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((m) => (
                <li key={m.id} className="row">
                  <span className="row-main">
                    <span className="row-title">{m.body}</span>
                    <span className="row-sub">
                      {m.direction === 'in' ? '←' : '→'} {formatStamp(m.createdAt, intlLocale, 'date')}
                      {m.byName ? ` · ${m.byName}` : ''}
                      {m.status === 'failed' ? <span className="badge badge-danger">{m.error}</span> : null}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </section>
  );
}
